import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowClockwise, XCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { clearRememberedActiveInterviewRoute } from "@/lib/activeInterviewNavigation";
import { toast } from "sonner";

export const COPY = {
  en: {
    restart: "Restart",
    paidOnly: "Available with 30-day full access",
    end: "End Interview",
    firstBody: "Start this interview again from Question 1. Your current answers and progress will be permanently deleted. This complimentary restart will not use another interview session.",
    paidBody: "You’ve already used your complimentary restart for this interview. Continuing will permanently delete your current answers and progress and use 1 interview session from your 30-day access.",
    noSessions: "No interview sessions left in your 30-day access",
    cancel: "Cancel",
    firstCta: "Restart",
    paidCta: "Restart and use 1 session",
    endBody: "End this interview and permanently delete your current answers and progress. This interview session will not be restored.",
    endCta: "End Interview",
    warmupEndBody: "If you end your interview you will use 1 of the interview sessions in your 30-day access. Do you want to continue?",
    yes: "YES",
    no: "NO",
    actionError: "Could not update the interview. Please try again.",
  },
  "pt-BR": {
    restart: "Reiniciar",
    paidOnly: "Disponível com o acesso completo de 30 dias",
    end: "Encerrar entrevista",
    firstBody: "Comece esta entrevista novamente a partir da Pergunta 1. Suas respostas e seu progresso atuais serão excluídos permanentemente. Esta reinicialização de cortesia não usará outra sessão de entrevista.",
    paidBody: "Você já usou a reinicialização de cortesia desta entrevista. Continuar excluirá permanentemente suas respostas e seu progresso atuais e usará 1 sessão de entrevista do seu acesso de 30 dias.",
    noSessions: "Nenhuma sessão de entrevista restante no seu acesso de 30 dias",
    cancel: "Cancelar",
    firstCta: "Reiniciar",
    paidCta: "Reiniciar e usar 1 sessão",
    endBody: "Encerre esta entrevista e exclua permanentemente suas respostas e seu progresso atuais. Esta sessão de entrevista não será restaurada.",
    endCta: "Encerrar entrevista",
    warmupEndBody: "Se você encerrar a entrevista, 1 das sessões de entrevista do seu acesso de 30 dias será utilizada. Deseja continuar?",
    yes: "SIM",
    no: "NÃO",
    actionError: "Não foi possível atualizar a entrevista. Tente novamente.",
  },
  "es-419": {
    restart: "Reiniciar",
    paidOnly: "Disponible con el acceso completo de 30 días",
    end: "Finalizar entrevista",
    firstBody: "Comience esta entrevista nuevamente desde la Pregunta 1. Sus respuestas y progreso actuales se eliminarán permanentemente. Este reinicio de cortesía no utilizará otra sesión de entrevista.",
    paidBody: "Ya utilizó el reinicio de cortesía de esta entrevista. Si continúa, se eliminarán permanentemente sus respuestas y progreso actuales y se utilizará 1 sesión de entrevista de su acceso de 30 días.",
    noSessions: "No le quedan sesiones de entrevista en su acceso de 30 días",
    cancel: "Cancelar",
    firstCta: "Reiniciar",
    paidCta: "Reiniciar y usar 1 sesión",
    endBody: "Finalice esta entrevista y elimine permanentemente sus respuestas y progreso actuales. Esta sesión de entrevista no se restaurará.",
    endCta: "Finalizar entrevista",
    warmupEndBody: "Si finaliza la entrevista, se utilizará 1 de las sesiones de entrevista de su acceso de 30 días. ¿Desea continuar?",
    yes: "SÍ",
    no: "NO",
    actionError: "No se pudo actualizar la entrevista. Inténtelo de nuevo.",
  },
  "fr-CA": {
    restart: "Recommencer",
    paidOnly: "Offert avec l’accès complet de 30 jours",
    end: "Mettre fin à l’entrevue",
    firstBody: "Recommencez cette entrevue à la question 1. Vos réponses et votre progression actuelles seront supprimées définitivement. Ce redémarrage gratuit n’utilisera pas une autre session d’entrevue.",
    paidBody: "Vous avez déjà utilisé le redémarrage gratuit de cette entrevue. Continuer supprimera définitivement vos réponses et votre progression actuelles et utilisera 1 session d’entrevue de votre accès de 30 jours.",
    noSessions: "Aucune session d’entrevue restante dans votre accès de 30 jours",
    cancel: "Annuler",
    firstCta: "Recommencer",
    paidCta: "Recommencer et utiliser 1 session",
    endBody: "Mettez fin à l’entrevue et supprimez définitivement vos réponses et votre progression actuels. Cette session d’entrevue ne sera pas rétablie.",
    endCta: "Mettre fin à l’entrevue",
    warmupEndBody: "Si vous mettez fin à l’entrevue, 1 des sessions d’entrevue de votre accès de 30 jours sera utilisée. Voulez-vous continuer?",
    yes: "OUI",
    no: "NON",
    actionError: "Impossible de mettre à jour l’entrevue. Veuillez réessayer.",
  },
};

function copyFor(lang) {
  const raw = String(lang || "en").toLowerCase();
  if (raw.startsWith("pt")) return COPY["pt-BR"];
  if (raw.startsWith("es")) return COPY["es-419"];
  if (raw.startsWith("fr")) return COPY["fr-CA"];
  return COPY.en;
}

function ConfirmModal({ title, body, cancel, confirm, danger = false, busy, onCancel, onConfirm }) {
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-6" onClick={() => !busy && onCancel()}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
        <p className="mt-4 text-sm leading-relaxed text-[color:var(--text-secondary)]">{body}</p>
        <div className="mt-7 flex justify-end gap-3">
          <button type="button" disabled={busy} onClick={onCancel} className="rounded-full border border-[color:var(--border-stone)] px-5 py-2.5 text-sm font-medium disabled:opacity-50">{cancel}</button>
          <button type="button" disabled={busy} onClick={onConfirm} className={`rounded-full px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50 ${danger ? "bg-[color:var(--score-bad)]" : "bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)]"}`}>{confirm}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function InterviewLifecycleControls() {
  const location = useLocation();
  const navigate = useNavigate();
  const match = location.pathname.match(/^\/interview\/([^/]+)$/);
  const sessionId = match?.[1] || null;
  const [target, setTarget] = useState(null);
  const [targetKind, setTargetKind] = useState(null);
  const [session, setSession] = useState(null);
  const [allowance, setAllowance] = useState(null);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setTarget(null);
      setTargetKind(null);
      return undefined;
    }
    let observer;
    let warmupHost = null;

    const applyInterviewAccessibility = () => {
      const progressSvg = document.querySelector('svg.-rotate-90');
      if (progressSvg) progressSvg.setAttribute("aria-hidden", "true");

      const quoteSvg = document.querySelector('[data-testid="question-text"] > svg');
      if (quoteSvg) quoteSvg.setAttribute("aria-hidden", "true");
    };

    const attachWarmup = () => {
      const gate = document.querySelector('[data-testid="warmup-gate"]');
      const header = gate?.querySelector("header");
      const row = header?.firstElementChild;
      if (!row) return false;

      row.className = "max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center gap-3";

      const img = row.querySelector("img");
      if (img) {
        img.src = "/brand/globe-logo.svg";
        img.alt = "";
        img.setAttribute("aria-hidden", "true");
        img.className = "h-[63px] w-[86px] object-contain";
      }

      const title = row.querySelector("span.font-semibold");
      if (title) {
        title.textContent = "ENGLISH JOB INTERVIEW SIMULATOR";
        title.className = "text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand)] leading-tight";
      }

      let host = row.querySelector('[data-testid="warmup-lifecycle-host"]');
      if (!host) {
        host = document.createElement("div");
        host.setAttribute("data-testid", "warmup-lifecycle-host");
        host.className = "ml-auto flex items-center gap-3";
        row.appendChild(host);
      }
      warmupHost = host;
      setTarget(host);
      setTargetKind("warmup");
      return true;
    };

    // Advanced Mode has its own header with a host for these controls (2026-11-02).
    const attachLive = () => {
      const host = document.querySelector('[data-testid="live-interview"] [data-testid="live-lifecycle-host"]');
      if (!host) return false;
      setTarget(host);
      setTargetKind("live");
      return true;
    };

    const attach = () => {
      if (attachLive()) return true;
      if (attachWarmup()) return true;

      const legacy = document.querySelector('[data-testid="restart-interview-btn"]');
      if (!legacy || !legacy.parentElement) return false;
      legacy.style.display = "none";
      applyInterviewAccessibility();
      setTarget(legacy.parentElement);
      setTargetKind("interview");
      return true;
    };

    attach();
    observer = new MutationObserver(() => {
      attach();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (observer) observer.disconnect();
      const legacy = document.querySelector('[data-testid="restart-interview-btn"]');
      if (legacy) legacy.style.display = "";
      if (warmupHost?.parentElement) warmupHost.remove();
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || targetKind !== "interview") return undefined;
    const priorTitle = document.title;
    const interviewTitle = "Interview Question | English Job Interview Simulator";
    document.title = interviewTitle;
    return () => {
      if (document.title === interviewTitle) document.title = priorTitle;
    };
  }, [sessionId, targetKind]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    Promise.all([
      api.get(`/sessions/${sessionId}`),
      api.get("/session-allowance"),
    ]).then(([sessionRes, allowanceRes]) => {
      if (cancelled) return;
      setSession(sessionRes.data);
      setAllowance(allowanceRes.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);

  const c = useMemo(() => copyFor(session?.interface_language || session?.language), [session]);
  const isFreeTrial = Boolean(session?.free_trial);
  const restartUsed = Number(session?.restart_count || 0) > 0;
  const hasPaidRestart = Boolean(allowance?.limit_disabled || (Number(allowance?.remaining || 0) > 0));
  // The free trial sees Restart greyed out, never hidden (design principle, 2026-10-30).
  const restartDisabled = isFreeTrial || (restartUsed && allowance && !hasPaidRestart);
  const isWarmup = targetKind === "warmup";

  const restart = async () => {
    if (!sessionId || busy || isFreeTrial) return;
    setBusy(true);
    window.__MEC_RESTARTING_SESSION__ = sessionId;
    try {
      await api.post(`/sessions/${sessionId}/restart-interview`);
      window.location.reload();
    } catch (err) {
      delete window.__MEC_RESTARTING_SESSION__;
      const message = err?.response?.data?.detail?.message || err?.response?.data?.detail || c.actionError;
      toast.error(typeof message === "string" ? message : c.actionError);
      setBusy(false);
      setModal(null);
      try {
        const { data } = await api.get("/session-allowance");
        setAllowance(data);
      } catch { /* ignore refresh failure */ }
    }
  };

  const endInterview = async () => {
    if (!sessionId || busy) return;
    setBusy(true);
    window.__MEC_ENDING_SESSION__ = sessionId;
    try {
      await api.post(`/sessions/${sessionId}/end-interview`);
      try { window.sessionStorage.removeItem(`mec_warmup_${sessionId}`); } catch { /* noop */ }
      clearRememberedActiveInterviewRoute();
      delete window.__MEC_ENDING_SESSION__;
      setModal(null);
      setBusy(false);
      navigate("/");
    } catch (err) {
      delete window.__MEC_ENDING_SESSION__;
      toast.error(err?.response?.data?.detail || c.actionError);
      setBusy(false);
      setModal(null);
    }
  };

  if (!sessionId || !target) return null;

  const controls = (
    <>
      {!isWarmup && (
        <div className="flex flex-col items-center">
          <button
            type="button"
            data-testid="restart-interview-lifecycle-btn"
            disabled={Boolean(restartDisabled)}
            onClick={() => !restartDisabled && setModal("restart")}
            className="inline-flex items-center gap-2 rounded-full border border-[color:var(--brand)] bg-transparent text-[color:var(--brand)] hover:bg-[color:var(--brand)]/5 px-4 py-2 text-sm disabled:border-[color:var(--border-stone)] disabled:bg-transparent disabled:text-[color:var(--text-secondary)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowClockwise size={16} /> {c.restart}
          </button>
          {restartDisabled && <span className="mt-1 max-w-40 text-center text-[10px] leading-tight text-[color:var(--text-secondary)]" data-testid="restart-unavailable-note">{isFreeTrial ? c.paidOnly : c.noSessions}</span>}
        </div>
      )}
      <button type="button" data-testid="end-interview-btn" onClick={() => setModal("end")} className="inline-flex items-center gap-2 rounded-full border border-[#9a4534]/50 text-[#9a4534] hover:bg-[#9a4534]/5 px-4 py-2 text-sm">
        <XCircle size={16} /> {c.end}
      </button>
    </>
  );

  return (
    <>
      <style>{`
        [data-testid="play-question-button"]:focus-visible,
        [data-testid="restart-interview-lifecycle-btn"]:focus-visible,
        [data-testid="end-interview-btn"]:focus-visible {
          outline: 3px solid var(--brand);
          outline-offset: 3px;
        }
      `}</style>
      {createPortal(controls, target)}
      {modal === "restart" && !isFreeTrial && !isWarmup && (
        <ConfirmModal title={c.restart} body={restartUsed ? c.paidBody : c.firstBody} cancel={c.cancel} confirm={restartUsed ? c.paidCta : c.firstCta} busy={busy} onCancel={() => setModal(null)} onConfirm={restart} />
      )}
      {modal === "end" && (
        <ConfirmModal
          title={c.end}
          body={isWarmup ? c.warmupEndBody : c.endBody}
          cancel={isWarmup ? c.no : c.cancel}
          confirm={isWarmup ? c.yes : c.endCta}
          danger
          busy={busy}
          onCancel={() => setModal(null)}
          onConfirm={endInterview}
        />
      )}
    </>
  );
}
